import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { isFormType, type FormType } from '../../../api/forms';
import { useEnumLabel } from '../../../api/enums';
import { isApiError } from '../../../api/errors';
import { TEMPLATE_FILE_MAX_BYTES, useImportTemplate, useTemplates } from '../../../api/templates';
import { Breadcrumbs, Button, ButtonGroup, ErrorSummary, FileUpload, WarningText, type ErrorSummaryItem } from '../../../components/idsk';
import { useNavigateWithToast } from '../../../components/common/navigateWithToast';
import { VersionConflictSummary } from '../../../components/common/VersionConflict';
import { useAnnouncer } from '../../../layout/Announcer';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { NotFoundPage } from '../../common/NotFoundPage';
import { LoadingPage } from '../../common/LoadingPage';
import { QueryError } from '../../common/QueryError';
import { RequireAdmin } from '../../common/RequireAdmin';

export const IMPORT_FILE_ID = 'template-file';

export function TemplateImportPage() {
  const { type } = useParams();
  if (!isFormType(type)) return <NotFoundPage />;
  return (
    <RequireAdmin>
      <TemplateImport type={type} />
    </RequireAdmin>
  );
}

/**
 * "Importovať zo súboru" (D32): a JSON template file becomes the draft of the template (replacing an existing draft).
 * The server checks the file and the definition like every draft; nothing is published – the admin reviews the draft
 * in the editor and the preview first.
 */
function TemplateImport({ type }: { type: FormType }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const { announce } = useAnnouncer();
  const templates = useTemplates();
  const importTemplate = useImportTemplate(type);
  const navigateWithToast = useNavigateWithToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const [errors, setErrors] = useState<ErrorSummaryItem[]>([]);
  const [fileError, setFileError] = useState<string | undefined>();
  const [conflict, setConflict] = useState(false);
  const [count, setCount] = useState(0);
  const name = label('formTypes', type);
  const title = t('templates.import.title');
  usePageTitle([title, name, t('templates.title')]);
  if (templates.error) return <QueryError error={templates.error} asPage />;
  if (templates.isPending) return <LoadingPage />;
  const draft = templates.data.find((x) => x.formType === type)?.draft ?? null;

  const show = (messages: string[], inline: string) => {
    setCount((c) => c + 1);
    setErrors(messages.map((text) => ({ targetId: IMPORT_FILE_ID, text })));
    setFileError(inline);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy.current) return;
    setConflict(false);
    const file = fileRef.current?.files?.[0];
    if (!file) return show([t('templates.import.required')], t('templates.import.required'));
    if (file.size > TEMPLATE_FILE_MAX_BYTES) return show([t('templates.import.tooLarge')], t('templates.import.tooLarge'));
    busy.current = true;
    announce(t('templates.import.importing', { file: file.name }));
    try {
      const content = await file.text();
      const result = await importTemplate.mutateAsync({ content, version: draft?.version });
      navigateWithToast(`/sablony/${type}/koncept`, t('templates.import.done', { file: file.name, n: result.versionNo }));
    } catch (err) {
      if (isApiError(err) && err.isVersionConflict) {
        setErrors([]);
        setFileError(undefined);
        setCount((c) => c + 1);
        setConflict(true);
      } else if (isApiError(err) && err.fieldErrors.length) {
        // problems of the file itself (field "file"), or of the definition in it (fields named by their keys)
        const ofFile = err.fieldErrors.every((fe) => fe.field === 'file');
        show(
          err.fieldErrors.map((fe) => fe.message),
          ofFile ? err.fieldErrors.map((fe) => fe.message).join(' ') : t('templates.import.definitionErrors'),
        );
      } else {
        const text = isApiError(err) ? err.message : t('templates.import.unreadable');
        show([text], text);
      }
    } finally {
      busy.current = false;
    }
  };

  return (
    <>
      <Breadcrumbs items={[{ text: t('nav.home'), to: '/' }, { text: t('templates.title'), to: '/sablony' }, { text: name, to: `/sablony/${type}` }, { text: title }]} />
      {conflict && <VersionConflictSummary onReload={() => void templates.refetch().then(() => setConflict(false))} focusKey={count} />}
      {errors.length > 0 && <ErrorSummary title={t('templates.import.errorsTitle')} errors={errors} focusKey={count} />}
      <PageHeading caption={name}>{title}</PageHeading>
      <p className="govuk-body">{t('templates.import.intro', { name })}</p>
      <p className="govuk-body">{t('templates.import.checks')}</p>
      {draft && <WarningText>{t('templates.import.replaceWarning', { n: draft.versionNo })}</WarningText>}
      <form onSubmit={(e) => void submit(e)} noValidate>
        <FileUpload
          id={IMPORT_FILE_ID}
          name="file"
          ref={fileRef}
          accept=".json,application/json"
          label={t('templates.import.fileLabel')}
          labelClassName="govuk-label--s"
          hint={t('templates.import.fileHint')}
          error={fileError}
          required
        />
        <ButtonGroup>
          <Button type="submit">{draft ? t('templates.import.submitReplace', { n: draft.versionNo }) : t('templates.import.submit')}</Button>
          <Link className="govuk-link" to={`/sablony/${type}`}>
            {t('common.cancel')}
          </Link>
        </ButtonGroup>
      </form>
    </>
  );
}
